const normalizePublicVariants = (variantsRaw: unknown, mealId: number): IDietMeal['variants'] =>
	(Array.isArray(variantsRaw) ? variantsRaw : []).map(v => {
		const mealSize = v.meal_size as { id?: number } | undefined
		const { meal_size: _ms, ...vRest } = v
		return {
			...vRest,
			meal_size_id: mealSize?.id ?? (vRest.meal_size_id as number | undefined) ?? 0,
			meal_id: mealId,
		}
	}) as IDietMeal['variants']

const normalizePublicAllergens = (allergensRaw: unknown): IDietMeal['allergens'] =>
	(Array.isArray(allergensRaw) ? allergensRaw : []).map(a => ({
		...a,
		name: transToLang(a.name_trans as string | null | undefined),
	})) as IDietMeal['allergens']

const normalizePublicFilterTags = (filterTagsRaw: unknown): IDietMeal['filter_tags'] =>
	(Array.isArray(filterTagsRaw) ? filterTagsRaw : []).map(t => ({
		...t,
		name: transToLang(t.name_trans as string | null | undefined),
		model_type: (t.model_type as IDietFilterTag['model_type']) ?? ('filter' as const),
	})) as IDietMeal['filter_tags']

const normalizePublicMedia = (mediaRaw: unknown): IDietMeal['media'] => {
	if (!Array.isArray(mediaRaw)) return null
	return mediaRaw.map((m: Record<string, unknown>) =>
		m.file_name !== undefined ? m : { ...m, file_name: m.name ?? '' },
	) as unknown as IDietMeal['media']
}

const buildMealKindFields = (rest: Record<string, unknown>) => ({
	internal_name: (rest.internal_name as string | null) ?? null,
	meal_type: (rest.meal_type as EMealType) ?? 'breakfast',
	meal_kind: (rest.meal_kind as EMealKind) ?? 'meal',
	meal_subkind: (rest.meal_subkind as EMealSubKind | null) ?? null,
	meal_category: (rest.meal_category as EMealCategory) ?? 'meal',
	addon_category: getAddonCategorySlug(rest.addon_category as IMealAddonCategoryRef | string | null),
	meal_price_tier: (rest.meal_price_tier as number) ?? 0,
})

const buildMealFlags = (rest: Record<string, unknown>) => ({
	serve_warm: (rest.serve_warm as boolean) ?? false,
	serve_cold: (rest.serve_cold as boolean) ?? false,
	is_kids: (rest.is_kids as boolean) ?? false,
	is_active: (rest.is_active as boolean) ?? true,
	has_same_package_size: (rest.has_same_package_size as boolean) ?? false,
	spicy_level: (rest.spicy_level as number) ?? 0,
	preparation_difficulty: (rest.preparation_difficulty as string | null) ?? null,
	min_order_qty: (rest.min_order_qty as number | null) ?? null,
	max_order_qty: (rest.max_order_qty as number | null) ?? null,
	active_to_order_from: (rest.active_to_order_from as string | null) ?? null,
	active_to_order_to: (rest.active_to_order_to as string | null) ?? null,
	average_rating_30_days: (rest.average_rating_30_days as number | null) ?? null,
	ratings_count_30_days: (rest.ratings_count_30_days as number) ?? 0,
})

const buildMealCollections = (rest: Record<string, unknown>) => ({
	dietary_groups: (rest.dietary_groups as IDietMeal['dietary_groups']) ?? [],
	components: (rest.components as IDietMeal['components']) ?? [],
	food_production_stages: (rest.food_production_stages as IDietMeal['food_production_stages']) ?? [],
	marketing_tags: (rest.marketing_tags as IDietMeal['marketing_tags']) ?? [],
	dietary_tags: (rest.dietary_tags as IDietMeal['dietary_tags']) ?? [],
	related_meals: (rest.related_meals as IDietMeal['related_meals']) ?? [],
	production_stage_assignments:
		(rest.production_stage_assignments as IDietMeal['production_stage_assignments']) ?? null,
	updated_at: (rest.updated_at as string | null) ?? null,
	created_at: (rest.created_at as string | null) ?? null,
})

/** Normalize public API shape (name_trans, variants.meal_size, allergens.name_trans, filter_tags.name_trans) to IDietMeal */
function normalizePublicMealToDietMeal(raw: Record<string, unknown>): IDietMeal {
	const id = raw.id as number
	const {
		name_trans: _nt,
		short_name_trans: _snt,
		description_trans: _dt,
		variants: _vr,
		allergens: _ar,
		filter_tags: _ft,
		price_tiers: _pt,
		ingredients: _ing,
		nutrihex_points: _np,
		has_meal_prep: _hmp,
		marketing_tags_trans: _mtt,
		filter_tags_trans: _ftt,
		media: _media,
		...rest
	} = raw

	return {
		...rest,
		id,
		name: transToLang(raw.name_trans as string | null | undefined),
		short_name: transToLang(raw.short_name_trans as string | null | undefined),
		description: raw.description_trans ? transToLang(raw.description_trans as string) : null,
		recipe: Array.isArray(raw.recipe)
			? (raw.recipe as ILangContent[])
			: transToLang(raw.recipe as string | null | undefined),
		...buildMealKindFields(rest),
		...buildMealFlags(rest),
		...buildMealCollections(rest),
		media: normalizePublicMedia(raw.media),
		allergens: normalizePublicAllergens(raw.allergens),
		variants: normalizePublicVariants(raw.variants, id),
		filter_tags: normalizePublicFilterTags(raw.filter_tags),
		product_line: (rest.product_line as EProductLine) ?? EProductLine.FOODIFY,
	} as IDietMeal
}

function toDietMealInput(json: IDietMeal | Record<string, unknown>): IDietMeal {
	const raw = json as Record<string, unknown>
	if ('name_trans' in raw) return normalizePublicMealToDietMeal(raw)
	if (raw.name !== undefined && !Array.isArray(raw.name)) return normalizePublicMealToDietMeal(raw)
	return json as IDietMeal
}

export const DietMealComponentPivot = (json: IDietMealComponentPivot): IDietMealComponentPivotModel => {
	if (json === null) {
		return {
			groupId: null,
			componentType: 'material' as EDietSharedComponentType,
			netWeight: 0,
			rawWeight: 0,
			weightAfterProcessing: 0,
			netWeightPerPiece: null,
			numberOfPieces: null,
			weightUnit: '',
		}
	}
	const {
		net_weight,
		raw_weight,
		weight_after_processing,
		weight_unit,
		component_type,
		group_id,
		net_weight_per_piece,
		number_of_pieces,
		...rest
	} = json

	return {
		...rest,
		groupId: group_id,
		componentType: component_type,
		netWeight: Number(net_weight),
		rawWeight: Number(raw_weight),
		weightAfterProcessing: Number(weight_after_processing),
		netWeightPerPiece: net_weight_per_piece !== null ? Number(net_weight_per_piece) : null,
		numberOfPieces: number_of_pieces !== null ? Number(number_of_pieces) : null,
		weightUnit: weight_unit,
	}
}

export const DietMealComponent = (json: IDietMealComponent): IDietMealComponentModel => {
	const {
		name,
		internal_name,
		warehouse_name,
		is_active,
		dietary_tags,
		allergens,
		pivot,
		nutritional_parameters,
		standardization_factor,
		...rest
	} = json

	return {
		...rest,
		name: safeLangContent(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
		allergens: safeMap(allergens, DietAllergen, []),
		dietaryTags: safeMap(dietary_tags, DietDietaryTag, []),
		nutritionalParameters: safeMap(nutritional_parameters, NutritionalParameterWithValue, []),
		pivot: DietMealComponentPivot(pivot ?? (null as unknown as IDietMealComponentPivot)),
		standardizationFactor: standardization_factor,
	}
}

const DietaryMealRelatedMeal = (json: IDietaryRelatedMeal): IDietaryRelatedMealModal => {
	const { id, name, media, relation_type } = json

	return {
		id,
		name: safeLangContent(name),
		media: safeMapOrNull(media, Media) ?? undefined,
		relationType: relation_type,
	}
}

export const DietMeal = (json: IDietMeal): IDietMealModel => {
	const normalized = toDietMealInput(json as IDietMeal | Record<string, unknown>)
	const {
		name,
		short_name,
		internal_name,
		description,
		recipe,
		meal_type,
		product_line,
		meal_kind,
		meal_subkind,
		meal_category,
		addon_category,
		meal_price_tier,
		min_order_qty,
		max_order_qty,
		serve_warm,
		serve_cold,
		is_kids,
		spicy_level,
		preparation_difficulty,
		is_active,
		active_to_order_from,
		active_to_order_to,
		has_same_package_size,
		average_rating_30_days,
		ratings_count_30_days,
		media,
		dietary_groups,
		allergens,
		variants,
		components,
		food_production_stages,
		filter_tags,
		marketing_tags,
		dietary_tags,
		related_meals,
		production_stage_assignments,
		updated_at,
		created_at,
		...rest
	} = normalized

	return {
		...rest,
		...DietAveragePricesToModel(rest),
		name: safeLangContent(name),
		shortName: safeLangContent(short_name),
		internalName: internal_name,
		description: Array.isArray(description) ? LangContentToModel(description) : null,
		recipe: Array.isArray(recipe) ? LangContentToModel(recipe) : null,
		isActive: is_active,
		activeToOrderFrom: active_to_order_from ? new Date(active_to_order_from) : null,
		activeToOrderTo: active_to_order_to ? new Date(active_to_order_to) : null,
		mealType: meal_type,
		productLine: product_line,
		mealKind: meal_kind,
		mealSubkind: meal_subkind,
		mealCategory: meal_category,
		addonCategory: getAddonCategorySlug(addon_category),
		mealPriceTier: meal_price_tier,
		minOrderQty: min_order_qty,
		maxOrderQty: max_order_qty,
		isServeWarm: serve_warm,
		isServeCold: serve_cold,
		isKids: is_kids,
		spicyLevel: spicy_level,
		preparationDifficulty: preparation_difficulty,
		hasSamePackageSize: has_same_package_size,
		averageRating: average_rating_30_days,
		ratingsCount: ratings_count_30_days,
		media: safeMapOrNull(media, Media),
		groups: safeMap(dietary_groups, DietDietaryGroup, []),
		allergens: safeMap(allergens, DietAllergen, []),
		variants: safeMap(variants, DietMealVariant, []),
		components: safeMap(components, DietMealComponent, []),
		foodProductionStages: safeMap(food_production_stages, DietProductionStep, []),
		filterTags: safeMap(filter_tags, DietFilterTag, []),
		marketingTags: safeMap(marketing_tags, DietMarketingTag, []),
		dietaryTags: safeMap(dietary_tags, DietDietaryTag, []),
		relatedMeals: safeMap(related_meals, DietaryMealRelatedMeal, []),
		productionStageAssignments: safeMapOrNull(production_stage_assignments, DietProductionStageAssignment),
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const DietMealType = (json: IDietMealType): IDietMealTypeModel => {
	const { meal_type, meal_count, ...rest } = json

	return {
		mealType: meal_type,
		mealCount: meal_count,
		...rest,
	}
}
